import type { BuildingUseType } from "../types/BuildingUseType";
import type { WizardProjectType } from "../types/WizardProjectType";

/** A row of the building area table: one building type with its own areas, floors and heated areas. */
export type BuildingAreaRow = BuildingUseType;

/** The project-level building fields a mixed-use table writes: summed areas, the largest floor counts, the main type. */
export type BuildingAreaTotals = {
  buildingTypeId: number | null;
  areaAboveGround: number | null;
  floorsAboveGround: number | null;
  heatedAreaAboveGround: number | null;
  areaBelowGround: number | null;
  floorsBelowGround: number | null;
  heatedAreaBelowGround: number | null;
};

export const grossArea = (row: BuildingAreaRow): number => (row.areaAboveGround ?? 0) + (row.areaBelowGround ?? 0);

const hasType = (row: BuildingAreaRow): boolean => row.buildingTypeId !== null && row.buildingTypeId !== undefined;

/** The typed row with the largest gross area, earlier row on a tie; 0 when no row has a type yet, -1 for no rows. */
export const mainRowIndex = (rows: BuildingAreaRow[]): number => {
  const main = rows.reduce(
    (best, row, index) => (hasType(row) && (best < 0 || grossArea(row) > grossArea(rows[best])) ? index : best),
    -1
  );
  return main < 0 && rows.length > 0 ? 0 : main;
};

/** The rows largest gross area first, the order a load shows and a save stores; a tie keeps the given order. */
export const byGrossAreaDescending = <T extends BuildingAreaRow>(rows: T[]): T[] =>
  rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => grossArea(b.row) - grossArea(a.row) || a.index - b.index)
    .map(({ row }) => row);

/** Each row's gross area out of every row's gross area, as a fraction; all 0 while there is no area yet. */
export const shares = (rows: BuildingAreaRow[]): number[] => {
  const total = rows.reduce((sum, row) => sum + grossArea(row), 0);
  return rows.map((row) => (total > 0 ? grossArea(row) / total : 0));
};

/** The row with every field present and `area` set to its gross area, the shape a load produces. */
export const normalizeRow = (row: BuildingAreaRow): BuildingAreaRow => ({
  buildingTypeId: row.buildingTypeId,
  area: grossArea(row),
  areaAboveGround: row.areaAboveGround ?? null,
  floorsAboveGround: row.floorsAboveGround ?? null,
  heatedAreaAboveGround: row.heatedAreaAboveGround ?? null,
  areaBelowGround: row.areaBelowGround ?? null,
  floorsBelowGround: row.floorsBelowGround ?? null,
  heatedAreaBelowGround: row.heatedAreaBelowGround ?? null,
});

const sumOrNull = (values: (number | null | undefined)[]): number | null =>
  values.every((value) => value === null || value === undefined)
    ? null
    : values.reduce<number>((sum, value) => sum + (value ?? 0), 0);

const maxOrNull = (values: (number | null | undefined)[]): number | null => {
  const present = values.filter((value): value is number => value !== null && value !== undefined);
  return present.length === 0 ? null : Math.max(...present);
};

export const projectTotalsFromRows = (rows: BuildingAreaRow[]): BuildingAreaTotals => {
  const main = mainRowIndex(rows);
  return {
    buildingTypeId: main < 0 ? null : rows[main].buildingTypeId,
    areaAboveGround: sumOrNull(rows.map((row) => row.areaAboveGround)),
    floorsAboveGround: maxOrNull(rows.map((row) => row.floorsAboveGround)),
    heatedAreaAboveGround: sumOrNull(rows.map((row) => row.heatedAreaAboveGround)),
    areaBelowGround: sumOrNull(rows.map((row) => row.areaBelowGround)),
    floorsBelowGround: maxOrNull(rows.map((row) => row.floorsBelowGround)),
    heatedAreaBelowGround: sumOrNull(rows.map((row) => row.heatedAreaBelowGround)),
  };
};

/** The table's rows: the stored uses of a mixed-use project, else one row read from the project-level fields. */
export const rowsFromProject = (project: WizardProjectType): BuildingAreaRow[] => {
  if ((project.buildingUses?.length ?? 0) >= 2) return project.buildingUses!;

  const { referencedAreas, heatArea } = project.emissionCalculationAssumptions;
  const buildingTypeId = project.buildingTypeEnum;
  return [
    normalizeRow({
      buildingTypeId: buildingTypeId === null || buildingTypeId === undefined ? null : Number(buildingTypeId),
      area: null,
      areaAboveGround: referencedAreas.areaAboveGround,
      floorsAboveGround: referencedAreas.floorsAboveGround,
      heatedAreaAboveGround: heatArea.heatedFloorAreaAboveGround,
      areaBelowGround: referencedAreas.areaBelowGround,
      floorsBelowGround: referencedAreas.basementFloors,
      heatedAreaBelowGround: heatArea.heatedBasementArea,
    }),
  ];
};
